import { csvParseRows, csvParse } from "d3-dsv"
import { groups, mean } from "d3-array"
import memoize from "lodash/memoize"
import { feature } from "topojson"
import { getCountryByCode } from "./utils/countryLookup"
import afterNextPaint from "./utils/afterNextPaint"

export const years = [
  "2015",
  "2016",
  "2017-18",
  "2019",
  "2020",
  "2021",
  "2022",
  "2023",
  "2024",
  "2025",
  "2026",
]

export const factors = [
  "Constraints on Government Powers",
  "Absence of Corruption",
  "Open Government",
  "Fundamental Rights",
  "Order and Security",
  "Regulatory Enforcement",
  "Civil Justice",
  "Criminal Justice",
]

export const defaultCountry = ""
export const defaultFactor = ""
// The newest year in `years`. Use this for anything tied to the current edition
// (download filenames, share pages, page copy) so a new year only means
// adding it to `years`.
export const latestYear = years[years.length - 1]
export const defaultYear = latestYear

export const groupAttributes = ["Region", "Income Group"]

const countryCodeValidation = {}

// Byte sizes of the files load() fetches, measured at build time (see
// vite.config.mjs). Browsers can't report download progress reliably when the
// server compresses a file, so progress compares the bytes read so far with
// these sizes instead.
const fileBytes =
  typeof __DATA_FILE_BYTES__ === "undefined" ? {} : __DATA_FILE_BYTES__

function averageFactors(groups, questionKeys, aggregateFactorKeys) {
  return groups.map(([groupKey, groupItems]) => {
    const groupResponses = {}

    // Calculate all mean values at once
    questionKeys.forEach((question) => {
      groupResponses[question] = mean(groupItems, (d) => d[question])
    })

    // Calculate aggregate totals for each factor
    aggregateFactorKeys.forEach((factorKey) => {
      const factorNumber = factorKey.match(/Factor ([0-9])/)
      if (factorNumber && factorNumber[1]) {
        const factor = factorNumber[1]
        const questions = questionKeys.filter(
          (questionKey) => questionKey[0] === factor,
        )
        groupResponses[factorKey] = mean(
          questions.map((q) => groupResponses[q]),
        )
      }
    })

    return { group: groupKey, ...groupResponses }
  })
}

// Highest score first; countries with no score go last and get no rank
function sortByScore(countries, key) {
  return [...countries].sort(
    (a, b) =>
      (a[key] === null) - (b[key] === null) || b[key] - a[key],
  )
}

class DataLoader {
  // Share of the data downloaded so far, from 0 to 1, or null if the file
  // sizes aren't known.
  progress = Object.keys(fileBytes).length ? 0 : null
  _progressListeners = new Set()
  _bytesRead = 0
  _bytesExpected = 0

  // Calls `listener` whenever the download progress changes. Returns a
  // function that stops the calls.
  onProgress(listener) {
    this._progressListeners.add(listener)
    return () => this._progressListeners.delete(listener)
  }

  _setProgress(progress) {
    this.progress = progress
    this._progressListeners.forEach((listener) => listener(progress))
  }

  _addBytes(count) {
    if (this.progress === null) return
    this._bytesRead += count
    this._setProgress(Math.min(1, this._bytesRead / this._bytesExpected))
  }

  // Fetches a file from public/ as text, counting bytes as they arrive.
  async _fetchText(file) {
    const response = await fetch(`${process.env.PUBLIC_URL}/${file}`)
    if (!response.body || !response.body.getReader) {
      const text = await response.text()
      this._addBytes(text.length)
      return text
    }
    const reader = response.body.getReader()
    const chunks = []
    let length = 0
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      length += value.length
      this._addBytes(value.length)
    }
    const bytes = new Uint8Array(length)
    let offset = 0
    for (const chunk of chunks) {
      bytes.set(chunk, offset)
      offset += chunk.length
    }
    return new TextDecoder().decode(bytes)
  }

  async _loadWorldMap() {
    const data = JSON.parse(await this._fetchText("map_50m.topojson.json"))
    const w = feature(data, data.objects.data)

    w.features = w.features.filter((d) => {
      d.properties.name = d.properties.WB_NAME
      d.id = getCountryByCode(d.properties.WB_A3)?.id //Remove the ? when generating PDFs
      return d.id !== undefined && d.id !== "010" && d.id !== "-99"
    })

    this.worldMap = w
  }

  async _loadCopy() {
    const text = await this._fetchText("copy.csv")
    this.copy = csvParse(text.trim())
  }

  async _loadPercentChange() {
    const text = await this._fetchText("data/latest_percent_change.csv")
    this.percentChange = csvParse(text.trim())
  }

  async _loadYear(year) {
    const text = await this._fetchText(`data/${year}.csv`)
    this.responsesByYear[year] = csvParseRows(text.trim())
  }

  _getCopy(key) {
    const row = this.copy.find((d) => d.Original === key)
    if (row) return row.Final
    console.warn("unfound copy key", key)
    return ""
  }

  getCopy = memoize(this._getCopy)

  _validate() {
    Object.keys(countryCodeValidation).forEach((country) => {
      const countryCodes = Object.keys(countryCodeValidation[country])
      if (countryCodes.length > 1) {
        console.warn(
          country,
          "has multiple country codes",
          countryCodeValidation[country],
        )
      }
    })
  }

  async load() {
    this.dataByYear = {}
    this.responsesByYear = {}

    const files = [
      "copy.csv",
      "data/latest_percent_change.csv",
      "map_50m.topojson.json",
      ...years.map((year) => `data/${year}.csv`),
    ]
    this._bytesRead = 0
    this._bytesExpected = files.reduce((sum, f) => sum + (fileBytes[f] || 0), 0)
    this._setProgress(files.every((f) => fileBytes[f]) ? 0 : null)

    // Request every file at once (the year files used to wait for the map)
    await Promise.all([
      this._loadCopy(),
      this._loadPercentChange(),
      this._loadWorldMap(),
      ...years.map((year) => this._loadYear(year)),
    ])

    // Processing blocks the page for a moment; let it show that the
    // downloads are done first
    await afterNextPaint()
    this.processData()
  }

  processData() {
    years.forEach((year, yearIndex) => {
      const rows = this.responsesByYear[year]
      let countries = []
      const questionKeys = []
      const aggregateFactorKeys = []

      rows.forEach((row, rowIndex) => {
        // Sub-factors up to 2024 are written "3.1. Name", from 2025 "3.1 Name".
        // Use one form so year-over-year lookups by name work.
        const header = row[0].trim().replace(/^([0-9]\.[0-9])\./, "$1")
        const columns = row.slice(1)

        if (header === "") return

        if (rowIndex === 0) {
          countries = [...columns]
            .filter((country) => country !== "")
            .map((country) => ({ [header]: country }))
        } else {
          columns.forEach((column, columnIndex) => {
            if (columnIndex >= countries.length) return
            let value = column

            const questionKeyMatches = header.match(
              /([0-9]\.[0-9])|(Factor [0-9])/,
            )
            if (questionKeyMatches) {
              const questionKey = questionKeyMatches[1] != null
              const factorKey = questionKeyMatches[2] != null

              if (questionKey && !questionKeys.includes(header))
                questionKeys.push(header)
              if (factorKey && !aggregateFactorKeys.includes(header))
                aggregateFactorKeys.push(header)

              // A few 2015/2016 sub-factor scores are blank: no score, not 0
              value = value.trim() === "" ? null : +value
            }

            if (header === "Abbreviation") {
              countries[columnIndex]["Country Code"] = value
              return
            }

            if (
              header === "Overall Score" ||
              header === "WJP Rule of Law Index: Overall Score"
            ) {
              value = +value
              countries[columnIndex].overall = value
            }

            countries[columnIndex][header] = value
          })
        }
      })

      countries.forEach((country) => {
        const name = country.Country
        const code = country["Country Code"]
        if (!countryCodeValidation[name]) {
          countryCodeValidation[name] = {}
        }
        countryCodeValidation[name][code] = true
      })

      questionKeys.push("overall")

      const yearlyData = {
        countries,
        groups: {},
        groupAverages: {},
        questionKeys,
        aggregateFactorKeys,
      }

      groupAttributes.forEach((groupAttribute) => {
        const grouped = groups(countries, (d) => d[groupAttribute])
        yearlyData.groups[groupAttribute] = grouped
        yearlyData.groupAverages[groupAttribute] = averageFactors(
          grouped,
          questionKeys,
          aggregateFactorKeys,
        )
      })

      const allQuestionKeys = [...questionKeys, ...aggregateFactorKeys]
      yearlyData.groupAverages.global = {}

      allQuestionKeys.forEach((questionKey) => {
        yearlyData.groupAverages.global[questionKey] = mean(
          countries,
          (c) => c[questionKey],
        )
      })

      let countriesInThePreviousYear = null
      const groupsToSort = [{ array: countries, key: "" }]
      if (yearIndex !== 0) {
        const previousYear = years[yearIndex - 1]
        const previousYearData = this.dataByYear[previousYear].countries
        countriesInThePreviousYear = countries.filter((c) =>
          previousYearData.find((p) => p.Country === c.Country),
        )

        groupsToSort.push({
          array: countriesInThePreviousYear,
          key: "_lastYear",
        })

        countriesInThePreviousYear.forEach((country) => {
          const previousYearCountry = previousYearData.find(
            (p) => p.Country === country.Country,
          )
          const thisYear = country.overall
          const lastYear = previousYearCountry.overall
          const difference = thisYear - lastYear
          country["overall_percentDifference"] = (difference / lastYear) * 100
        })
      }

      allQuestionKeys.forEach((questionKey) => {
        groupsToSort.forEach(({ array, key }) => {
          const sortedArray = sortByScore(array, questionKey)

          sortedArray.forEach((country, countryIndex) => {
            country[`${questionKey}_Rank_Global${key}`] =
              country[questionKey] === null ? null : countryIndex + 1
            country[`Count_Global${key}`] = sortedArray.length
          })

          groupAttributes.forEach((groupAttribute) => {
            const groups = yearlyData.groups[groupAttribute]
            groups.forEach(([_, countriesInGroup]) => {
              const sortedGroup = sortByScore(countriesInGroup, questionKey)
              sortedGroup.forEach((country, countryIndex) => {
                country[`${questionKey}_Rank_${groupAttribute}${key}`] =
                  country[questionKey] === null ? null : countryIndex + 1
                country[`Count_${groupAttribute}${key}`] = sortedGroup.length
              })
            })
          })
        })
      })

      this.dataByYear[year] = yearlyData
    })

    this._validate()
    this.setupYearsForCountries()
    this.processPercentChange()
  }

  setupYearsForCountries() {
    this.yearsForCountries = {}
    Object.keys(this.dataByYear).forEach((year) => {
      const countries = this.dataByYear[year].countries
      countries.forEach((country) => {
        if (!this.yearsForCountries[country.Country]) {
          this.yearsForCountries[country.Country] = []
        }
        this.yearsForCountries[country.Country].push(year)
      })
    })

    Object.keys(this.yearsForCountries).forEach((country) => {
      this.yearsForCountries[country].sort()
    })
  }

  processPercentChange() {
    this.percentChange.forEach((percentChangeRow) => {
      const latestYearData = this.dataByYear[years[years.length - 1]]
      const percentChangeCountry = percentChangeRow["Country/Jurisdiction"]
      const matchingCountry = latestYearData.countries.find(
        (d) => d.Country === percentChangeCountry,
      )

      if (matchingCountry) {
        const value = percentChangeRow["% Score Change"].trim()
        matchingCountry.externalPercentChange = value.length
          ? parseFloat(value)
          : null
      }
    })
  }
}

const dataLoaderInstance = new DataLoader()
export default dataLoaderInstance
